// app/javascript/src/components/Scheduling/tabs/GeneralTab.tsx
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import copy from 'copy-to-clipboard'
import styled from 'styled-components'
import Panel from '../../Panel/Panel'
import PanelAction from '../../Panel/PanelAction'
import Alert from '../../Alert/Alert'
import Switch from '../../Form/Switch'
import PowerSelect from '../../Form/PowerSelect'
import ReactSelectTheme from '../../Form/ReactSelectTheme'
import Editor, { DEFAULT_EDITOR_CONFIG } from '../../Editor/Editor'
import EditorContainer from '../../Editor/EditorContainer'
import Notification from '../../../utilities/Notification'
import ERoute from '../../../ERoute'
import { EventType } from '../../../types'
import { sanitizeSlugInput, slugify } from '../slug'
import UrlInput from '../UrlInput'
import LocationsEditor from './LocationsEditor'

export interface TabProps {
  draft: EventType
  onChange: (patch: Partial<EventType>) => void
}

interface GeneralProps extends TabProps {
  hostBookingSlug: string
  onHostBookingSlugChange: (value: string) => void
  bookingHost: string
  canEditHostSlug: boolean
}

const HostSlugButton = styled.a`
  flex-shrink: 0;
  color: #637381;
  text-decoration: underline dotted;

  &.is-disabled {
    text-decoration: none;
    pointer-events: none;
  }
`

const HostSlugInput = styled.div`
  width: 128px;
  flex-shrink: 0;
`

const DURATION_PRESETS = [5, 10, 15, 20, 25, 30, 45, 50, 60, 75, 80, 90, 120, 150, 180, 240, 300, 360, 420, 480]

const DESCRIPTION_EDITOR_CONFIG = {
  ...DEFAULT_EDITOR_CONFIG,
  toolbarButtons: ['paragraphFormat', 'bold', 'italic', 'insertLink', 'formatUL', 'formatOL'],
  heightMin: 120,
}

export default function GeneralTab({ draft, onChange, hostBookingSlug, onHostBookingSlugChange, bookingHost, canEditHostSlug }: GeneralProps) {
  const { t } = useTranslation()
  const [editingUserSlug, setEditingUserSlug] = React.useState(false)
  const connected = draft.connected_calendar_types || []
  const multipleDurations = draft.durations.length > 0

  const slugsUnchanged = !!draft.public_url && draft.public_url.endsWith(`/${hostBookingSlug}/${draft.slug}`)
  const copyUrl = slugsUnchanged ? draft.public_url! : `https://${bookingHost}/${hostBookingSlug}/${draft.slug}`
  const [durationText, setDurationText] = React.useState(String(draft.duration))
  React.useEffect(() => { if (Number(durationText) !== draft.duration) setDurationText(String(draft.duration)) }, [draft.duration])

  const onCopyUrlClick = () => {
    copy(copyUrl)
    Notification.notifySuccess(t('Scheduling::Link copied'))
  }

  // PowerSelect's typings don't support isMulti, so react-select hands us an array here
  const onDurationsChange = (value: unknown) => {
    const next = ((value || []) as Array<{ value: string }>).map(option => Number(option.value)).sort((a, b) => a - b)
    if (next.length === 0) return
    onChange({ durations: next, duration: next.includes(draft.duration) ? draft.duration : next[0] })
  }

  const onMultipleDurationsToggle = () => {
    onChange({ durations: multipleDurations ? [] : Array.from(new Set([draft.duration, 15, 30, 60])).sort((a, b) => a - b) })
  }

  const durationPresetOptions = Array.from(new Set([...DURATION_PRESETS, ...draft.durations])).sort((a, b) => a - b)
    .map(d => ({ value: String(d), label: t('Scheduling::{{count}} minutes', { count: d }) }))
  const durationOptions = draft.durations.map(d => ({ value: String(d), label: t('Scheduling::{{count}} minutes', { count: d }) }))

  return (
    <>
      <Panel>
        <div className='form-item'>
          <label>{t('Scheduling::Title')}</label>
          <input type='text' value={draft.title} onChange={e => onChange({ title: e.currentTarget.value })} />
        </div>

        <div className='form-item'>
          <label>{t('Scheduling::Description')}</label>
          <EditorContainer>
            <Editor
              model={draft.description}
              onModelChange={(description: string) => onChange({ description })}
              config={{ ...DESCRIPTION_EDITOR_CONFIG, placeholderText: t('Scheduling::A quick video meeting.') }}
            />
          </EditorContainer>
        </div>

        <div className='form-item'>
          <label>{t('Scheduling::URL')}</label>
          <UrlInput>
            <span>{bookingHost}/</span>
            {editingUserSlug && canEditHostSlug ? (
              <HostSlugInput>
                <input
                  type='text'
                  aria-label={t('Scheduling::Your booking page')}
                  value={hostBookingSlug}
                  onChange={e => onHostBookingSlugChange(sanitizeSlugInput(e.currentTarget.value))}
                  onBlur={() => onHostBookingSlugChange(slugify(hostBookingSlug))}
                  autoFocus
                />
              </HostSlugInput>
            ) : (
              <HostSlugButton href='javascript://' className={canEditHostSlug ? '' : 'is-disabled'} onClick={() => setEditingUserSlug(true)}>
                {hostBookingSlug}
              </HostSlugButton>
            )}
            <span>/</span>
            <input
              type='text'
              value={draft.slug}
              onChange={e => onChange({ slug: sanitizeSlugInput(e.currentTarget.value) })}
              onBlur={() => onChange({ slug: slugify(draft.slug) })}
            />
            <PanelAction icon='copy' onClick={onCopyUrlClick} />
          </UrlInput>
        </div>
      </Panel>

      <Panel title={t('Scheduling::Duration')}>
        {multipleDurations ? (
          <>
            <div className='form-item'>
              <label>{t('Scheduling::Durations')}</label>
              <PowerSelect
                // @ts-ignore
                isMulti
                isClearable={false}
                closeMenuOnSelect={false}
                options={durationPresetOptions}
                value={durationPresetOptions.filter(option => draft.durations.includes(Number(option.value)))}
                onChange={onDurationsChange}
                theme={ReactSelectTheme}
              />
            </div>
            <div className='form-item'>
              <label>{t('Scheduling::Default duration')}</label>
              <PowerSelect
                options={durationOptions}
                value={durationOptions.find(option => option.value === String(draft.duration))}
                onChange={(option) => option && onChange({ duration: Number(option.value) })}
                theme={ReactSelectTheme}
              />
            </div>
          </>
        ) : (
          <div className='form-item'>
            <label>{t('Scheduling::Minutes')}</label>
            <input
              type='number'
              min={1}
              max={1440}
              value={durationText}
              onChange={e => {
                const value = e.currentTarget.value
                setDurationText(value)
                const n = Number(value)
                if (value !== '' && Number.isInteger(n) && n >= 1 && n <= 1440) onChange({ duration: n })
              }}
              onBlur={() => setDurationText(String(draft.duration))}
            />
          </div>
        )}

        <Switch
          name='multiple-durations'
          checked={multipleDurations}
          onClick={onMultipleDurationsToggle}
          label={t('Scheduling::Allow multiple durations')}
        />
      </Panel>

      <Panel title={t('Scheduling::Location')}>
        {connected.length === 0 && (
          <div className='form-item'>
            <Alert
              type='info'
              text={<>
                {t('Scheduling::Connect Google Calendar or Outlook to add a Google Meet or Microsoft Teams link to your bookings.')}{' '}
                <Link to={ERoute.PATH_INTEGRATIONS}>{t('Scheduling::Go to integrations')}</Link>
              </>}
            />
          </div>
        )}

        <LocationsEditor
          locations={draft.locations}
          connectedCalendarTypes={connected}
          onChange={locations => onChange({ locations })}
        />
      </Panel>
    </>
  )
}
